import useScrollAnimation from '../../hooks/useScrollAnimation';

interface ProcessStep {
  number: string;
  title: string;
  description: string;
  icon: string;
}

interface ProcessSectionProps {
  eyebrow?: string;
  headline: string;
  steps: ProcessStep[];
}

const ProcessSection = ({ eyebrow = 'How It Works', headline, steps }: ProcessSectionProps) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-[#0a0a0a] py-24 lg:py-32 overflow-hidden">
      {/* Subtle grain texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\' opacity=\'0.15\'/%3E%3C/svg%3E")',
        }}
      />
      <div className="relative max-w-screen-xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div ref={ref} className={`anim-section ${isVisible ? 'is-visible' : ''} mb-20`}>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div>
              {eyebrow && (
                <p className="text-[#c8a951] text-xs uppercase tracking-[0.35em] font-semibold mb-4">{eyebrow}</p>
              )}
              <h2
                className="text-4xl lg:text-5xl font-bold text-white leading-tight"
                style={{ fontFamily: '"Playfair Display", serif' }}
              >
                {headline}
              </h2>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-10 h-px bg-[#c8a951]/40" />
              <a
                href="#contact"
                className="text-[#c8a951] text-xs uppercase tracking-[0.2em] font-semibold whitespace-nowrap hover:text-[#e0c070] transition-colors cursor-pointer"
              >
                Start Your Project
              </a>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connecting line - desktop only */}
          <div className="hidden lg:block absolute top-11 left-11 right-11 h-px">
            <div className="w-full h-full bg-gradient-to-r from-transparent via-[#c8a951]/35 to-transparent" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-4">
            {steps.map((step, i) => (
              <ProcessStepCard key={i} step={step} index={i} />
            ))}
          </div>
        </div>

        {/* Bottom CTA bar */}
        <div className="mt-16 pt-10 border-t border-[#c8a951]/15 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5">
          <p className="text-white/40 text-sm leading-relaxed max-w-lg">
            Every project receives a dedicated certified installer and a clear written timeline before work begins.
          </p>
          <a
            href="tel:3204931454"
            className="inline-flex items-center gap-3 bg-[#c8a951] text-[#0a0a0a] text-xs font-bold px-8 py-4 uppercase tracking-[0.18em] hover:bg-[#e0c070] transition-colors whitespace-nowrap cursor-pointer shrink-0"
          >
            <i className="ri-phone-fill" />
            320.493.1454 — Free Estimate
          </a>
        </div>
      </div>
    </section>
  );
};

const ProcessStepCard = ({ step, index }: { step: ProcessStep; index: number }) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <div
      ref={ref}
      className={`anim-section ${isVisible ? 'is-visible' : ''} group`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      {/* Number + icon row */}
      <div className="relative flex items-center mb-5">
        <div className="relative z-10 w-[88px] h-[88px] flex items-center justify-center border border-[#c8a951]/30 bg-[#0a0a0a] group-hover:border-[#c8a951]/70 transition-colors duration-400 shrink-0">
          <div className="w-10 h-10 flex items-center justify-center">
            <i className={`${step.icon} text-xl text-[#c8a951]`} />
          </div>
          {/* Step number badge */}
          <span className="absolute -top-3 -right-3 w-7 h-7 bg-[#c8a951] text-[#0a0a0a] text-[10px] font-bold flex items-center justify-center">
            {step.number}
          </span>
        </div>
      </div>

      <h3
        className="text-base font-bold text-white mb-3 leading-snug"
        style={{ fontFamily: '"Playfair Display", serif' }}
      >
        {step.title}
      </h3>
      <p className="text-white/40 text-sm leading-relaxed">{step.description}</p>

      {/* Active line at bottom */}
      <div className="mt-5 h-px w-0 bg-[#c8a951] group-hover:w-full transition-all duration-500" />
    </div>
  );
};

export default ProcessSection;
